// @ts-nocheck
'use client';

import { useState, useEffect } from 'react';
import { PlusIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { makeRequest } from 'services/request.service';
import { showAlert } from 'services/alerts.service';
import TableComponent from 'components/common/TableComponent';
import ButtonComponent from 'components/common/ButtonComponent';
import GroupModal from 'components/modals/GroupModal';
import ActionCardComponent from 'components/common/ActionCardComponent';
import TooltipComponent from 'components/common/TooltipComponent';

import { useAuth } from 'auth/AuthContext';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

const GroupsManagement = ({ onBlockGroupsUpdate }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [state, setState] = useState({
    blockGroups: [],
    error: null,
    isOpenGroupModal: false,
    selectedGroup: null,
    groupsSearchTerm: ''
  });

  const getBlockGroups = async () => {
    try {
      const response = await makeRequest({
        method: 'GET',
        url: `/management/farming-blocks/block-groups/${user.farmId}`,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('secret')}`
        }
      });

      const groups = Array.isArray(response.data) ? response.data : [];
      setState(s => ({
        ...s,
        blockGroups: groups,
        error: null
      }));
      // Notify parent component of block groups update
      if (onBlockGroupsUpdate) {
        onBlockGroupsUpdate(groups);
      }
    } catch (err) {
      showAlert({ message: t('groups.failedToLoadGroups'), icon: 'error' });
      setState(s => ({
        ...s,
        error: t('groups.failedToLoadGroups'),
        blockGroups: []
      }));
    }
  };

  const handleSaveGroup = async groupData => {
    try {
      const isEditing = !!state?.selectedGroup?.blockGroupId;

      const method = isEditing ? 'PUT' : 'POST';
      const url = isEditing
        ? `/management/farming-blocks/block-groups/${state.selectedGroup.blockGroupId}`
        : `/management/farming-blocks/block-groups/${user.farmId}`;

      const response = await makeRequest({
        method,
        url,
        headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` },
        data: groupData,
        alertResult: true
      });

      if (response?.status === 200 || response?.status === 201) {
        await getBlockGroups();
        setState(s => ({
          ...s,
          isOpenGroupModal: false,
          selectedGroup: null
        }));
      }
    } catch (err) {
      showAlert({ message: t('groups.failedToSaveGroup'), icon: 'error' });
      setState(s => ({
        ...s,
        error: t('groups.failedToSaveGroup')
      }));
    }
  };

  const handleDelete = async group => {
    try {
      const response = await makeRequest({
        method: 'DELETE',
        url: `/management/farming-blocks/block-groups/${group.blockGroupId || group.groupId}`,
        headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` },
        alertResult: true
      });
      if (response?.status === 200) {
        getBlockGroups();
      }
    } catch (error) {
      showAlert({ text: t('groups.failedToDeleteGroup'), icon: 'error' });
    }
  };

  const onToogleGroupModal = (group = null) => {
    setState(s => ({
      ...s,
      isOpenGroupModal: !s.isOpenGroupModal,
      selectedGroup: group
    }));
  };

  const filteredGroups = state.blockGroups.filter(group =>
    (group.groupName || '').toLowerCase().includes(state.groupsSearchTerm)
  );

  const groupsTableData = {
    columns: [
      { key: 'blockGroupId', label: '#' },
      { key: 'groupName', label: t('groups.groupName') },
      {
        key: 'actions',
        label: t('common.manage'),
        cell: (value, group) => (
          <div className="flex items-center gap-2">
            <button
              onClick={() => onToogleGroupModal(group)}
              title={t('common.edit')}
              className="p-1 rounded transition"
            >
              <PencilSquareIcon className="w-5 h-5 text-blue-500" />
            </button>
            <button
              onClick={() => handleDelete(group)}
              title={t('common.delete')}
              className="p-1 rounded transition"
            >
              <TrashIcon className="w-5 h-5 text-red-500" />
            </button>
          </div>
        )
      }
    ],
    rows: filteredGroups
  };

  useEffect(() => {
    getBlockGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <ActionCardComponent>
        <input
          type="text"
          value={state.groupsSearchTerm}
          placeholder={t('groups.filterGroups')}
          onChange={e => setState(s => ({ ...s, groupsSearchTerm: e.target.value.toLowerCase() }))}
          className="search-input"
          data-align="left"
        />

        {!!user.farmId && (
          <TooltipComponent content={t('groups.tooltipNewGroup')}>
            <ButtonComponent onClick={() => onToogleGroupModal()}>
              <PlusIcon className="w-5 h-5 mr-1" />
              {t('groups.newGroup')}
            </ButtonComponent>
          </TooltipComponent>
        )}
      </ActionCardComponent>

      <TableComponent tableData={groupsTableData} />

      {/* group modal */}
      {state.isOpenGroupModal && (
        <GroupModal
          isOpen
          handleClose={onToogleGroupModal}
          handleSave={handleSaveGroup}
          selectedGroup={state.selectedGroup}
        />
      )}
    </>
  );
};

GroupsManagement.propTypes = {
  onBlockGroupsUpdate: PropTypes.func
};

export default GroupsManagement;
